import { Component, ErrorInfo, ReactNode } from "react";
import "./ErrorBoundary.scss";

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

// Render-time errors anywhere below this boundary (a bad API response, a malformed prop, etc.)
// would otherwise unmount the entire React tree to a blank page with no way to recover. This
// catches them and shows a reload prompt instead. Error boundaries only catch render/lifecycle
// errors - they don't catch errors inside event handlers or rejected promises, which is why
// data-fetching call sites still need their own .catch() handling.
class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("Unhandled render error caught by ErrorBoundary:", error, errorInfo);
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="error-boundary-fallback">
          <h1>Something went wrong.</h1>
          <p>An unexpected error occurred. Reloading the page usually fixes it.</p>
          <button type="button" onClick={this.handleReload}>
            Reload page
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
